import React from 'react';
import './themed-container.css';
import agentTheme from '../../projects/remote-agent/imgs/theme.webp';
import automationsTheme from '../../projects/automations/imgs/theme.webp';
import medsTheme from '../../projects/weight-loss-medication/imgs/theme.webp';

const PAD = 'var(--spacing-themed-padding)';
const RADIUS = 'var(--border-radius-md)';
const DEFAULT_PADDING = '1 1 1 1';

const NAMED_THEMES = {
    agent: agentTheme,
    automations: automationsTheme,
    meds: medsTheme,
};

function isCssColor(value) {
    if (typeof value !== 'string') return false;
    const color = value.trim();
    if (/^(#|rgb|hsl|var\()/i.test(color)) return true;
    return typeof CSS !== 'undefined' && CSS.supports?.('color', color);
}

function resolveTheme(theme) {
    if (!theme) return null;
    if (NAMED_THEMES[theme]) {
        return { backgroundImage: `url(${NAMED_THEMES[theme]})` };
    }
    if (isCssColor(theme)) {
        return { backgroundColor: theme };
    }
    return { backgroundImage: `url(${theme})` };
}

function expandMask(mask) {
    const parts = String(mask ?? DEFAULT_PADDING).trim().split(/\s+/);
    if (parts.length === 1) {
        return { top: parts[0], right: parts[0], bottom: parts[0], left: parts[0] };
    }
    if (parts.length === 2) {
        return { top: parts[0], right: parts[1], bottom: parts[0], left: parts[1] };
    }
    if (parts.length === 3) {
        return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[1] };
    }
    return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] };
}

function isOn(value) {
    return value === '1' || value === 1;
}

function isShadowOn(value) {
    return value !== 'off' && value !== false && value !== 0 && value !== '0';
}

function toPad(value) {
    return isOn(value) ? PAD : '0';
}

function cornerRadius(a, b) {
    return isOn(a) && isOn(b) ? RADIUS : '0';
}

/** '16' | '16 32' | '16 16 32 16' (px, CSS corner order) | 16 → CSS border-radius. */
export function toRadius(value) {
    if (value == null || value === '') return undefined;
    if (typeof value === 'number') return `${value}px`;
    return String(value)
        .trim()
        .split(/\s+/)
        .map((part) => (/^-?\d*\.?\d+$/.test(part) ? `${part}px` : part))
        .join(' ');
}

function toMaxWidth(value) {
    if (value == null || value === '') return undefined;
    return typeof value === 'number' ? `${value}px` : value;
}

function toAlignItems(align) {
    if (align === 'end') return 'flex-end';
    if (align === 'start') return 'flex-start';
    return undefined;
}

/** When a vertical pad side is off, pin content to that edge so grown frames don't look padded. */
function alignFromPadding(sides) {
    const top = isOn(sides.top);
    const bottom = isOn(sides.bottom);
    if (top && !bottom) return 'flex-end';
    if (!top && bottom) return 'flex-start';
    return undefined;
}

/** Same as above for horizontal sides. */
function justifyFromPadding(sides) {
    const left = isOn(sides.left);
    const right = isOn(sides.right);
    if (left && !right) return 'flex-end';
    if (!left && right) return 'flex-start';
    return undefined;
}

export default function ThemedContainer({ theme, padding = DEFAULT_PADDING, span = 'full', shadow = 'on', maxWidth, align, radius, children, className }) {
    const hasContent = React.Children.count(children) > 0;
    const sides = expandMask(padding);
    const alignItems = toAlignItems(align) ?? alignFromPadding(sides);
    const justifyContent = justifyFromPadding(sides);
    const classes = [
        'themed-container',
        hasContent ? null : 'themed-container--empty',
        span === 'half' ? 'themed-container--half' : 'themed-container--full',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const style = {
        padding: `${toPad(sides.top)} ${toPad(sides.right)} ${toPad(sides.bottom)} ${toPad(sides.left)}`,
        ...(alignItems ? { alignItems, height: '100%' } : {}),
        ...(justifyContent ? { justifyContent } : {}),
        ...resolveTheme(theme),
    };

    const contentMaxWidth = toMaxWidth(maxWidth);
    const radii = hasContent
        ? [
              cornerRadius(sides.top, sides.left),
              cornerRadius(sides.top, sides.right),
              cornerRadius(sides.bottom, sides.right),
              cornerRadius(sides.bottom, sides.left),
          ]
        : null;
    // No padding → still round the image (shadow off shouldn't kill radius).
    const autoRadius = radii
        ? (radii.every((r) => r === '0') ? RADIUS : radii.join(' '))
        : undefined;
    const contentRadius = hasContent ? (toRadius(radius) ?? autoRadius) : undefined;

    const contentStyle = {
        borderRadius: contentRadius,
        ...(contentMaxWidth ? { width: '100%', maxWidth: `min(100%, ${contentMaxWidth})` } : {}),
    };

    return (
        <div className={classes} style={style}>
            {hasContent ? (
                <div
                    className={[
                        'themed-container__content',
                        isShadowOn(shadow) ? 'themed-container__content--shadow' : null,
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    style={contentStyle}
                >
                    {children}
                </div>
            ) : null}
        </div>
    );
}
